/**
 * Vendor portal — Product editor (create + edit).
 * Manages all editable fields on a store_products row, including image
 * uploads to the store-images bucket under {user_id}/products/{slug}/...
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useVendor } from "@/lib/store/useVendor";
import { useAuth } from "@/lib/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Trash2, Upload, X } from "lucide-react";

type Category = { id: string; name: string; slug: string };
type ProductRow = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  product_type: "physical" | "digital";
  status: string;
  images: string[];
  base_price_cents: number;
  currency: string;
  category_id: string | null;
  digital_file_url: string | null;
  digital_delivery_email: string | null;
  weight_grams: number | null;
  shipping_origin_country: string | null;
  stock_quantity: number | null;
  unlimited_stock: boolean;
  low_stock_threshold: number | null;
  metadata: Record<string, unknown> | null;
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const VendorProductEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { user } = useAuth();
  const { vendor } = useVendor();
  const { toast } = useToast();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  const [form, setForm] = useState<ProductRow>({
    id: "",
    slug: "",
    title: "",
    subtitle: null,
    description: null,
    product_type: "physical",
    status: "draft",
    images: [],
    base_price_cents: 0,
    currency: "usd",
    category_id: null,
    digital_file_url: null,
    digital_delivery_email: null,
    weight_grams: null,
    shipping_origin_country: null,
    stock_quantity: null,
    unlimited_stock: true,
    low_stock_threshold: null,
    metadata: {},
  });

  useEffect(() => {
    void supabase
      .from("store_categories")
      .select("id, name, slug")
      .eq("active", true)
      .order("sort_order")
      .then(({ data }) => setCategories((data as Category[]) ?? []));
  }, []);

  useEffect(() => {
    if (isNew || !id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("store_products")
        .select(
          "id, slug, title, subtitle, description, product_type, status, images, base_price_cents, currency, category_id, digital_file_url, digital_delivery_email, weight_grams, shipping_origin_country, stock_quantity, unlimited_stock, low_stock_threshold, metadata",
        )
        .eq("id", id)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        toast({ title: "Not found", variant: "destructive" });
        navigate("/store/vendor/products");
        return;
      }
      setForm(data as ProductRow);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate, toast]);

  const set = <K extends keyof ProductRow>(key: K, value: ProductRow[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const priceDollars = useMemo(
    () => (form.base_price_cents / 100).toFixed(2),
    [form.base_price_cents],
  );

  const onPriceChange = (v: string) => {
    const num = Number(v);
    if (!Number.isNaN(num) && num >= 0) {
      set("base_price_cents", Math.round(num * 100));
    }
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files?.length || !user || !vendor) return;
    setUploading(true);
    const newUrls: string[] = [];
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop() ?? "jpg";
      const path = `${user.id}/products/${vendor.slug}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage
        .from("store-images")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (error) {
        toast({ title: "Upload failed", description: error.message, variant: "destructive" });
        continue;
      }
      const { data } = supabase.storage.from("store-images").getPublicUrl(path);
      newUrls.push(data.publicUrl);
    }
    set("images", [...form.images, ...newUrls]);
    setUploading(false);
  };

  const removeImage = (url: string) => {
    set("images", form.images.filter((u) => u !== url));
  };

  const physicalOutOfStock =
    form.product_type === "physical" &&
    !form.unlimited_stock &&
    (form.stock_quantity ?? 0) <= 0;

  const handleSave = async (publish = false) => {
    if (!vendor) return;
    if (!form.title.trim()) {
      toast({ title: "Title required", variant: "destructive" });
      return;
    }
    if (form.base_price_cents <= 0) {
      toast({ title: "Price must be greater than zero", variant: "destructive" });
      return;
    }
    if (publish && physicalOutOfStock) {
      toast({
        title: "Out of stock",
        description: "Set a stock quantity, mark unlimited, or add an in-stock variant before publishing.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    const slug = form.slug || slugify(form.title);
    const nextStatus = publish
      ? "active"
      : form.status === "active"
        ? physicalOutOfStock
          ? "out_of_stock"
          : "active"
        : form.status === "out_of_stock"
          ? "out_of_stock"
          : "draft";
    const payload = {
      vendor_id: vendor.id,
      slug,
      title: form.title,
      subtitle: form.subtitle,
      description: form.description,
      product_type: form.product_type,
      status: nextStatus,
      images: form.images,
      base_price_cents: form.base_price_cents,
      currency: form.currency,
      category_id: form.category_id,
      digital_file_url: form.product_type === "digital" ? form.digital_file_url : null,
      digital_delivery_email:
        form.product_type === "digital" ? form.digital_delivery_email : null,
      weight_grams: form.product_type === "physical" ? form.weight_grams : null,
      shipping_origin_country:
        form.product_type === "physical" ? form.shipping_origin_country : null,
      stock_quantity: form.unlimited_stock ? null : form.stock_quantity,
      unlimited_stock: form.unlimited_stock,
      low_stock_threshold: form.unlimited_stock ? null : form.low_stock_threshold,
      published_at: publish ? new Date().toISOString() : undefined,
    };

    if (isNew) {
      const { data, error } = await supabase
        .from("store_products")
        .insert(payload as never)
        .select("id")
        .single();
      setSaving(false);
      if (error) {
        toast({ title: "Save failed", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: publish ? "Published!" : "Saved as draft" });
      navigate(`/store/vendor/products/${data.id}`);
    } else {
      const { error } = await supabase
        .from("store_products")
        .update(payload as never)
        .eq("id", form.id);
      setSaving(false);
      if (error) {
        toast({ title: "Save failed", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: publish ? "Published!" : "Changes saved" });
    }
  };

  const handleDelete = async () => {
    if (!form.id || !confirm("Delete this product? This cannot be undone.")) return;
    const { error } = await supabase.from("store_products").delete().eq("id", form.id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Deleted" });
    navigate("/store/vendor/products");
  };

  if (loading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/store/vendor/products")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to products
        </Button>
        {!isNew && (
          <Badge variant="secondary" className="capitalize">
            {form.status.replace(/_/g, " ")}
          </Badge>
        )}
      </div>

      <Card className="p-5 space-y-4">
        <h2 className="font-semibold">Basics</h2>
        <div className="space-y-2">
          <Label>Title *</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Subtitle</Label>
          <Input
            value={form.subtitle ?? ""}
            onChange={(e) => set("subtitle", e.target.value || null)}
          />
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea
            rows={5}
            value={form.description ?? ""}
            onChange={(e) => set("description", e.target.value || null)}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select
              value={form.product_type}
              onValueChange={(v) => set("product_type", v as "physical" | "digital")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="physical">Physical</SelectItem>
                <SelectItem value="digital">Digital</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select
              value={form.category_id ?? ""}
              onValueChange={(v) => set("category_id", v || null)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose…" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Price (USD) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={priceDollars}
              onChange={(e) => onPriceChange(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Inventory</h2>
          {physicalOutOfStock && <Badge variant="destructive">Out of stock</Badge>}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.unlimited_stock}
            onChange={(e) => set("unlimited_stock", e.target.checked)}
          />
          Track unlimited stock (no inventory limit)
        </label>
        {!form.unlimited_stock && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Stock quantity</Label>
              <Input
                type="number"
                min="0"
                value={form.stock_quantity ?? ""}
                onChange={(e) =>
                  set(
                    "stock_quantity",
                    e.target.value === "" ? null : Math.max(0, Number(e.target.value)),
                  )
                }
              />
              <p className="text-xs text-muted-foreground">
                When this hits zero the product is auto-marked out of stock and hidden from buyers.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Low-stock alert threshold</Label>
              <Input
                type="number"
                min="0"
                value={form.low_stock_threshold ?? ""}
                onChange={(e) =>
                  set(
                    "low_stock_threshold",
                    e.target.value === "" ? null : Math.max(0, Number(e.target.value)),
                  )
                }
                placeholder="e.g. 5"
              />
            </div>
          </div>
        )}
        {form.product_type === "physical" && (
          <p className="text-xs text-muted-foreground">
            For products with size/color options, leave this unlimited and manage stock per variant.
          </p>
        )}
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Images</h2>
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleUpload(e.target.files)}
              disabled={uploading}
            />
            <span className="inline-flex items-center gap-2 text-sm rounded-md border px-3 py-1.5 hover:bg-muted">
              <Upload className="h-4 w-4" />
              {uploading ? "Uploading…" : "Add images"}
            </span>
          </label>
        </div>
        {form.images.length === 0 ? (
          <p className="text-sm text-muted-foreground">No images yet.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {form.images.map((url) => (
              <div key={url} className="relative group aspect-square rounded-md overflow-hidden bg-muted">
                <img src={url} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(url)}
                  className="absolute top-1 right-1 bg-background/80 rounded p-1 opacity-0 group-hover:opacity-100 transition"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {form.product_type === "digital" ? (
        <Card className="p-5 space-y-3">
          <h2 className="font-semibold">Digital delivery</h2>
          <div className="space-y-2">
            <Label>Download URL</Label>
            <Input
              value={form.digital_file_url ?? ""}
              onChange={(e) => set("digital_file_url", e.target.value || null)}
              placeholder="https://…"
            />
            <p className="text-xs text-muted-foreground">
              Buyers receive this link after a successful purchase.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Delivery email (optional)</Label>
            <Input
              value={form.digital_delivery_email ?? ""}
              onChange={(e) => set("digital_delivery_email", e.target.value || null)}
              placeholder="orders@yourshop.com"
            />
          </div>
        </Card>
      ) : (
        <Card className="p-5 space-y-3">
          <h2 className="font-semibold">Shipping</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Weight (grams)</Label>
              <Input
                type="number"
                min="0"
                value={form.weight_grams ?? ""}
                onChange={(e) =>
                  set("weight_grams", e.target.value ? Number(e.target.value) : null)
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Origin country (ISO)</Label>
              <Input
                value={form.shipping_origin_country ?? ""}
                onChange={(e) => set("shipping_origin_country", e.target.value || null)}
                placeholder="US"
                maxLength={2}
              />
            </div>
          </div>
        </Card>
      )}

      <Card className="p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <Button onClick={() => handleSave(false)} disabled={saving} variant="outline">
            <Save className="h-4 w-4 mr-2" /> Save draft
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving}>
            {form.status === "active" ? "Save & keep live" : "Publish"}
          </Button>
        </div>
        {!isNew && (
          <Button variant="ghost" onClick={handleDelete} className="text-destructive">
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </Button>
        )}
      </Card>
    </div>
  );
};

export default VendorProductEditor;
